import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

// MUI Components
import {
  Select,
  MenuItem,
  ListSubheader,
  Typography,
  Tooltip,
} from "@mui/material";

// Icons
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import CabinIcon from "@mui/icons-material/Cabin";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import CalculateIcon from "@mui/icons-material/Calculate";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CachedIcon from "@mui/icons-material/Cached";
import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";

// Components & Utils
import { Selector } from "../../Tools/index.js";
import { Colors } from "../../Assets/Config/color.config";
import { getConstByStandard } from "../utils/getConstFileByStandard.js";
import { categoryRouteMap } from "../services/categoryRouteMap.js";
import {
  EMISSION_REMOVAL_MITIGATION_ENABLED,
  getEnabledEmissionCategoryOptions,
  isEmissionRemovalMitigation,
} from "../utils/emissionRemovalMitigation.js";

// Stores
import emissionViewStore from "../../Store/marco/emissionViewStore.js";
import commonStore from "../../Store/marco/commonStore.js";

// Styles
import styles from "../styles/emissionActivityViewHeader.module.css";

/** Search debounce delay in milliseconds */
const SEARCH_DEBOUNCE_DELAY = 300;

/** Default unit ID for emission calculations (21 = tCO₂e) */
const DEFAULT_UNIT_ID = 21;

/** Default GWP ID for AR6 */
const DEFAULT_GWP_AR6 = 3;

/** GWP options used by Emission Removal & Mitigation */
const RM_GWP_OPTIONS = [
  { label: "AR5", value: 2 },
  { label: "AR6", value: 3 },
];
const RM_GWP_TOOLTIP =
  "Emission results are shown using the GWP set selected here. Switching sets changes the figures displayed; it does not recalculate your stored data. Special gases such as HFCs and PFCs are not covered by this selection — their GWP values are fixed.";

const CUSTOM_GWP_TOOLTIP = [
  "Standard EF sources carry their own GWP. This setting does not change them.",
  "Custom emission factors have no source to follow, so they use the version selected here.",
  "Switching AR6 / AR5 only changes custom EF results.",
];

/** Special industry code that requires hiding actions */
const SPECIAL_INDUSTRY_CODE = "8550";

/** Category code that requires special GWP handling */
const SPECIAL_CATEGORY_CODE = "1_4";

/** View types that don't show category selector and search */
const RESTRICTED_VIEWS = ["Year", "Entity"];

/** View types that show refresh button */
const REFRESHABLE_VIEWS = [
  "Emission Activity",
  "Data Entry",
  "Emission Factor",
  "Emission",
  "Emission Reduction",
  "Summary",
];

/** View types that show Excel import/export button */
const EXCEL_ENABLED_VIEWS = [
  "Emission Activity",
  "Data Entry",
  "Emission Factor",
];

const DEFAULT_NAVIGATION_BUTTONS = [
  { label: "Year", icon: <InsertInvitationIcon /> },
  { label: "Entity", icon: <AccountTreeIcon /> },
  { label: "Emission Activity", icon: <CabinIcon /> },
  { label: "Data Entry", icon: <NoteAltIcon /> },
  { label: "Emission Factor", icon: <CalculateIcon /> },
  { label: "Emission", icon: <StackedBarChartIcon /> },
];

const RM_NAVIGATION_BUTTONS = [
  { label: "Year", icon: <InsertInvitationIcon /> },
  { label: "Entity", icon: <AccountTreeIcon /> },
  {
    label: "Emission Reduction Activity",
    view: "Emission Activity",
    icon: <CabinIcon />,
  },
  {
    label: "Emission Reduction",
    view: "Emission Reduction",
    icon: <CalculateIcon />,
  },
  { label: "Summary", view: "Summary", icon: <StackedBarChartIcon /> },
];

/** Mapping from view names to Excel modal types */
const VIEW_TO_EXCEL_TYPE_MAP = {
  "Data Entry": "dataEntry",
  "Emission Factor": "ef",
};

/** MUI Select component common styling */
const MUI_SELECT_STYLES = {
  "& .MuiSelect-icon": {
    color: "green",
  },
  "& .MuiSelect-select": {
    padding: "2px 10px",
  },
};

const RM_GWP_SELECT_STYLES = {
  ...MUI_SELECT_STYLES,
  height: "32px",
  boxShadow: "none",
  "& .MuiOutlinedInput-notchedOutline": {
    top: 0,
  },
  "& .MuiOutlinedInput-notchedOutline legend": {
    display: "none",
  },
  "&.Mui-focused": {
    outline: "none",
    boxShadow: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d0d5dd",
    borderWidth: "1px",
  },
};

const CUSTOM_GWP_SELECT_STYLES = {
  ...RM_GWP_SELECT_STYLES,
  minWidth: "148px",
  backgroundColor: "#fff",
  "& .MuiSelect-select": {
    padding: "7px 32px 7px 12px",
  },
};

const shouldHideAction = ({
  hasAccess,
  isActionDisabled,
  selectedView,
  industryCode,
  rootId,
}) => {
  if (!hasAccess) return true;
  if (isActionDisabled) return true;

  // Special case: hide for Emission Activity view with industry code 8550
  if (
    selectedView === "Emission Activity" &&
    industryCode === SPECIAL_INDUSTRY_CODE
  ) {
    return true;
  }

  // YouanYouan test
  // if (rootId === SPECIAL_ACCOUNT) {
  //   return true;
  // }

  return false;
};

const getExcelModalType = (selectedView, selectedTab) => {
  if (selectedView === "Emission Activity") {
    return categoryRouteMap[selectedTab] || "";
  }
  return VIEW_TO_EXCEL_TYPE_MAP[selectedView] || "";
};

const shouldShowFormArea = (selectedView) => {
  return !RESTRICTED_VIEWS.includes(selectedView);
};

const shouldShowRefreshButton = (selectedView) => {
  return REFRESHABLE_VIEWS.includes(selectedView);
};

const shouldShowExcelButton = (selectedView) => {
  return EXCEL_ENABLED_VIEWS.includes(selectedView);
};

export const EmissionActivityViewHeader = observer(() => {
  const store = emissionViewStore;
  const cmStore = commonStore;
  const { t } = useTranslation("ActivityMain");


  const CONST = getConstByStandard(store.standardId);
  const categoryOptions = getEnabledEmissionCategoryOptions(
    CONST.categoryDropdown,
  );
  const categoryCode = CONST?.category[store.selectedTab]?.substring(3);
  const isCategoryOne = categoryCode?.startsWith("1_");
  const isSpecialCategory = categoryCode === SPECIAL_CATEGORY_CODE;
  const authCompanyKey = JSON.stringify(store.authComArr);
  const isRMWorkflow =
    EMISSION_REMOVAL_MITIGATION_ENABLED &&
    isEmissionRemovalMitigation(store.selectedScope);
  const navigationButtons = isRMWorkflow
    ? RM_NAVIGATION_BUTTONS
    : DEFAULT_NAVIGATION_BUTTONS;
  const selectedView = store.selectedView || DEFAULT_NAVIGATION_BUTTONS[0].label;

  // Find current scope configuration
  const currentScopeConfig = cmStore?.EACategory?.find(
    (item) => item.label === store.selectedScope,
  );

  // Check if action buttons are disabled for current category
  const isActionDisabled = currentScopeConfig?.category?.find(
    (item) => item.label === store.selectedTab,
  )?.isDisabled;

  const [unitId, setUnitId] = useState(DEFAULT_UNIT_ID);
  const [gwpId, setGwpId] = useState(DEFAULT_GWP_AR6);
  const [isGwpTooltipOpen, setIsGwpTooltipOpen] = useState(false);
  const [recalcCooldown, setRecalcCooldown] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const timeoutIdRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const cooldownIntervalRef = useRef(null);

  // Update emission info when unit or GWP changes
  useEffect(() => {
    store._handleSetEmissionInfo(gwpId, unitId);
  }, [gwpId, unitId]);

  // GWP is shared across Scope 1 Category 1 tabs, but resets for a new year.
  useEffect(() => {
    setGwpId(DEFAULT_GWP_AR6);
  }, [store.selectedYear]);

  // Preserve the existing behavior outside Scope 1 Category 1.
  useEffect(() => {
    if (!isCategoryOne) setGwpId(DEFAULT_GWP_AR6);
  }, [categoryCode, isCategoryOne]);

  // Count against an unfiltered 1.4 Emission Factor List.
  useEffect(() => {
    if (
      selectedView !== "Emission" ||
      !isSpecialCategory ||
      !store.selectedYear
    ) {
      return;
    }

    store.getCustomEFCount();
  }, [
    selectedView,
    isSpecialCategory,
    store.selectedYear,
    store.standardId,
    authCompanyKey,
    store,
  ]);

  // Initialize and cleanup search on mount/unmount
  useEffect(() => {
    store._handleSetSearchLabel("");
    store._handleSetSearchKeyword("");

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    };
  }, []);

  const handleDebouncedSearch = (searchValue) => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(() => {
      store._handleSetSearchKeyword(searchValue);
    }, SEARCH_DEBOUNCE_DELAY);
  };

  const handleClearSearch = () => {
    store._handleSetSearchLabel("");
    store._handleSetSearchKeyword("");
  };

  const handleViewChange = (viewLabel) => {
    if (!store.selectedYear) return;
    store._handleSetSelectedView(viewLabel);
  };

  const handleExcelButtonClick = () => {
    const modalType = getExcelModalType(store.selectedView, store.selectedTab);
    cmStore._handleSetIsShowExcelImportExportModal(true, modalType);
  };

  const renderNavigationButtons = () => (
    <div className={styles.buttons}>
      {navigationButtons.map((button, index) => {
        const viewValue = button.view || button.label;

        return (
          <div className={styles.buttonWrap} key={index}>
            <button
              className={`${styles.button} ${
                selectedView === viewValue ? styles.blue : ""
              }`}
              onClick={() => handleViewChange(viewValue)}
            >
              {button.icon}
            </button>
            <p>{t(button.label)}</p>
          </div>
        );
      })}
    </div>
  );

  // Emission 頁面專用：點擊後呼叫重新計算流程，並鎖定按鈕 30 秒
  const handleEmissionRefresh = async () => {
    if (recalcCooldown) return;

    setRecalcCooldown(true);
    setCooldownSeconds(30);

    // 每秒遞減倒數
    if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    cooldownIntervalRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownIntervalRef.current);
          cooldownIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // 30 秒後解鎖按鈕
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => {
      setRecalcCooldown(false);
    }, 30000);

    try {
      await store.recalculateEmission(cmStore.rootId);
    } catch (err) {
      console.error("Emission recalculate failed:", err);
    }
  };

  const renderRefreshButton = () => {
    if (!shouldShowRefreshButton(store.selectedView)) return null;

    // Emission 頁面：走重新計算流程 + 30 秒 CD
    if (store.selectedView === "Emission") {
      return (
        <div
          className={styles.refresh}
          onClick={handleEmissionRefresh}
          style={
            recalcCooldown
              ? { backgroundColor: "gray", cursor: "not-allowed", pointerEvents: "none" }
              : undefined
          }
        >
          <CachedIcon />
          {recalcCooldown
            ? `0:${String(cooldownSeconds).padStart(2, "0")}`
            : t("Refresh")}
        </div>
      );
    }

    // 其他頁面：原有行為
    return (
      <div className={styles.refresh} onClick={() => store._handleRefresh()}>
        <CachedIcon />
        {t("Refresh")}
      </div>
    );
  };

  const renderCategorySelector = () => (
    <Selector
      width="350px"
      small
      value={categoryOptions.filter(
        (option) => option.value === store.selectedScope,
      )}
      getOptionLabel={(e) => (
        <div style={{ display: "flex", textAlign: "left" }}>
          <span style={{ marginLeft: 5 }}>{t(e.label)}</span>
        </div>
      )}
      menuListColor={Colors.Dark[0]}
      options={categoryOptions}
      _handleSelect={(e) => {
        store._handleSetSelectedScope(e.value);
      }}
    />
  );

  const renderSearchBar = () => (
    <div className={styles.searchWrap}>
      <input
        className={styles.search}
        value={store.searchLabel}
        onChange={(e) => {
          store._handleSetSearchLabel(e.target.value);
          handleDebouncedSearch(e.target.value);
        }}
        placeholder={t("Search...")}
      />
      {store.searchLabel && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClearSearch}
        >
          <ClearIcon />
        </button>
      )}
    </div>
  );

  const renderUnitSelector = () => (
    <div
      className={`${styles.unitSelect} ${
        isSpecialCategory ? styles.unitSelectWithCustomGwp : ""
      }`}
    >
      <Typography>{t("Emission Unit")}</Typography>
      <Select
        value={unitId}
        onChange={(event) => setUnitId(event.target.value)}
        sx={MUI_SELECT_STYLES}
      >
        <MenuItem value={20}>kgCO₂e</MenuItem>
        <MenuItem value={21}>tCO₂e</MenuItem>
      </Select>
    </div>
  );

  const getCustomEFCountDisplay = () => {
    if (store.customEFCountStatus === "error") return "—";
    if (store.customEFCountStatus !== "success") return "…";
    return store.customEFCount;
  };

  const renderGwpSelector = () => {
    const validOptions = [2, 3];
    const safeGwpId = validOptions.includes(gwpId)
      ? gwpId
      : DEFAULT_GWP_AR6;

    if (isSpecialCategory) {
      return (
        <div className={`${styles.ARselect} ${styles.customGwpSelect}`}>
          <Typography>GWP</Typography>
          <Tooltip
            arrow
            open={isGwpTooltipOpen}
            onClose={() => setIsGwpTooltipOpen(false)}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <div className={styles.gwpTooltipContent}>
                {CUSTOM_GWP_TOOLTIP.map((line) => (
                  <p key={line}>{t(line)}</p>
                ))}
              </div>
            }
          >
            <button
              type="button"
              className={styles.gwpInfoButton}
              aria-label={t("About GWP selection")}
              aria-expanded={isGwpTooltipOpen}
              onMouseEnter={() => setIsGwpTooltipOpen(true)}
              onMouseLeave={() => setIsGwpTooltipOpen(false)}
              onFocus={() => setIsGwpTooltipOpen(true)}
              onBlur={() => setIsGwpTooltipOpen(false)}
              onClick={() => setIsGwpTooltipOpen(true)}
            >
              <InfoIcon color="primary" />
            </button>
          </Tooltip>

          <div className={styles.customGwpControl}>
            <Select
              value={safeGwpId}
              onChange={(event) => setGwpId(Number(event.target.value))}
              renderValue={(value) => (Number(value) === 2 ? "AR5" : "AR6")}
              sx={CUSTOM_GWP_SELECT_STYLES}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: 250,
                    mt: 0.5,
                    border: "1px solid #d9dde3",
                    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.12)",
                  },
                },
              }}
            >
              <MenuItem value={3} className={styles.gwpMenuItem}>
                <span>AR6</span>
                <span className={styles.gwpDefaultLabel}>{t("Default")}</span>
              </MenuItem>
              <MenuItem value={2} className={styles.gwpMenuItem}>
                <span>AR5</span>
              </MenuItem>
              <ListSubheader
                component="div"
                disableSticky
                className={styles.gwpMenuFooter}
              >
                {t("Standard EF sources are not affected.")}
              </ListSubheader>
            </Select>
            <span className={styles.customGwpNote}>
              {t("Applies to custom EF only")} ({getCustomEFCountDisplay()})
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.ARselect}>
        <Typography>GWP</Typography>
        <Select
          value={safeGwpId}
          onChange={(event) => setGwpId(Number(event.target.value))}
          sx={MUI_SELECT_STYLES}
        >
          <MenuItem value={2}>AR5</MenuItem>
          <MenuItem value={3}>AR6</MenuItem>
        </Select>
      </div>
    );
  };

  const renderRMGwpSelector = () => {
    if (!isRMWorkflow || !shouldShowRefreshButton(store.selectedView)) {
      return null;
    }

    const safeGwpId = RM_GWP_OPTIONS.some((option) => option.value === gwpId)
      ? gwpId
      : DEFAULT_GWP_AR6;

    return (
      <div className={styles.rmGwpSelect}>
        <Typography>GWP</Typography>
        <Tooltip title={t(RM_GWP_TOOLTIP)} arrow>
          <button
            type="button"
            className={styles.rmGwpInfoButton}
            aria-label={t("About GWP selection")}
          >
            i
          </button>
        </Tooltip>
        <Select
          value={safeGwpId}
          onChange={(event) => setGwpId(Number(event.target.value))}
          sx={RM_GWP_SELECT_STYLES}
        >
          {RM_GWP_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
    );
  };

  const renderEmissionControls = () => {
    if (store.selectedView !== "Emission") return null;

    return (
      <>
        {renderUnitSelector()}
        {renderGwpSelector()}
      </>
    );
  };

  const renderExcelButton = () => {
    if (!shouldShowExcelButton(store.selectedView)) return null;
    if (isRMWorkflow) return null;

    const hideButton = shouldHideAction({
      hasAccess: cmStore.access !== false,
      isActionDisabled,
      selectedView: store.selectedView,
      industryCode: cmStore.industryCode,
      rootId: cmStore.rootId,
    });

    if (hideButton) return null;

    return (
      <div className={styles.excelFunction} onClick={handleExcelButtonClick}>
        {t("Export")} / {t("Import")}
      </div>
    );
  };

  const renderFormArea = () => {
    if (!shouldShowFormArea(store.selectedView)) return null;

    return (
      <div className={styles.formArea}>
        {renderCategorySelector()}
        {renderSearchBar()}
        {renderEmissionControls()}
        {renderExcelButton()}
      </div>
    );
  };

  //////// main render
  return (
    <div className={styles.container}>
      <div className={styles.wrap}>
        {renderNavigationButtons()}
        {renderRMGwpSelector()}
        {renderRefreshButton()}
      </div>
      {renderFormArea()}
    </div>
  );
});
